"use client";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import {
  motion,
  useMotionTemplate,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { rise } from "@/lib/animation/variant";
import { useCalBooking } from "@/providers/booking/CalBookingProvider";

// Cal's embed is ~440 KB of JS plus its own fonts and timezone table. Loaded
// through next/dynamic with ssr off and mounted only once the booker is within
// scroll range, so it costs nothing on first load of any route.
const Cal = dynamic(() => import("@calcom/embed-react"), { ssr: false });

const subscribeViewport = (cb: () => void) => {
  window.addEventListener("resize", cb);
  return () => window.removeEventListener("resize", cb);
};

const BookingEmbedCard = () => {
  const {
    enabled,
    calLink,
    namespace,
    rescheduleUid,
    embedKey,
    requestCal,
    calReady,
  } = useCalBooking();
  const hostRef = useRef<HTMLDivElement>(null);
  const [inRange, setInRange] = useState(false);
  const reduce = useReducedMotion();
  const isDesktop = useSyncExternalStore(
    subscribeViewport,
    () => window.innerWidth >= 1024,
    () => false,
  );

  const { scrollYProgress } = useScroll({
    target: hostRef,
    offset: ["start end", "end start"],
  });
  const maxBlur = reduce || !isDesktop ? 0 : 10;
  const rawBlur = useTransform(
    scrollYProgress,
    [0, 0.15, 0.85, 1],
    [maxBlur, 0, 0, maxBlur],
  );
  const blur = useSpring(rawBlur, {
    stiffness: 120,
    damping: 24,
    restDelta: 0.01,
  });
  const filter = useMotionTemplate`blur(${blur}px)`;

  // Ask for the SDK as soon as the browser is idle after `load`. This component
  // only renders on a page that has the booker, so the fetch is never paid by a
  // route that does not need it — but waiting for the scroll to get within
  // range meant the visitor watched an empty frame while 440 KB and the cal.com
  // iframe handshake happened in series. Idle time before that is free.
  useEffect(() => {
    let idle = 0;
    const warm = () => {
      // Cal's iframe comes from app.cal.com: open that socket with the request
      // so the TLS handshake is not in front of the first booker paint.
      if (!document.querySelector('link[data-cal-preconnect]')) {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = "https://app.cal.com";
        link.crossOrigin = "";
        link.dataset.calPreconnect = "true";
        document.head.appendChild(link);
      }

      const schedule =
        window.requestIdleCallback ??
        ((cb: IdleRequestCallback) => window.setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 1));
      idle = schedule(() => requestCal(), { timeout: 2000 });
    };

    if (document.readyState === "complete") warm();
    else window.addEventListener("load", warm, { once: true });

    return () => {
      window.cancelIdleCallback?.(idle);
      window.removeEventListener("load", warm);
    };
  }, [requestCal]);

  // The mount itself still waits for the booker to be within scroll range, two
  // screens out, so the iframe is never built for someone who never gets there.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || inRange) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setInRange(true);
        requestCal();
        observer.disconnect();
      },
      { rootMargin: "1600px" },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [inRange, requestCal]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const silence = () => {
      const frame = host.querySelector("iframe");
      if (!frame) return false;
      frame.setAttribute("scrolling", "no");
      return true;
    };

    if (silence()) return;

    const observer = new MutationObserver(() => {
      if (silence()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [embedKey]);

  if (!enabled || !calLink) return null;

  return (
    <motion.div
      ref={hostRef}
      variants={rise}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      custom={3}
      style={maxBlur ? { filter } : undefined}
      className="relative mx-auto w-full max-w-6xl"
    >
      {/* `calReady` gates the mount, not just the import: the iframe reads the
          namespace's theme at init, so mounting before `cal("ui")` has applied
          renders the booker in Cal's default purple for a frame. */}
      {/* Placeholder while the SDK and the cal.com iframe land. Before this the
          section was simply empty for the gap, which reads as broken; the
          heights are the booker's own measured ones so the shift when it
          arrives is as small as Cal's own self-resize allows. */}
      {!(inRange && calReady) && (
        <div
          aria-hidden="true"
          className="min-h-140 w-full animate-pulse rounded-sm border-2 border-black bg-card-foreground lg:min-h-143"
        />
      )}

      {inRange && calReady && (
        <Cal
          key={embedKey}
          namespace={namespace}
          calLink={calLink}
          className="w-full"
          style={{ width: "100%", height: "auto", overflow: "hidden" }}
          config={{
            layout: "month_view",
            theme: "dark",
            ...(rescheduleUid ? { rescheduleUid } : {}),
          }}
        />
      )}
    </motion.div>
  );
};

export default BookingEmbedCard;
